import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert, Box, Button, CircularProgress, DialogContent, IconButton, InputAdornment, List, ListItemButton, Stack,
    TextField, Typography
} from '@mui/material';
import {useDebouncer} from '@tanstack/react-pacer';
import {ProfileListItem} from 'crqlar-react-common/dist/models/profile/profileListItem.model';
import {Survey} from 'crqlar-react-common/dist/models/survey/survey.model';
import {ProfileAggregationService} from 'crqlar-react-common/dist/services/aggregation/profileAggregation.service';
import {useReadOnly} from 'crqlar-react-common/dist/utils/hooks/users/useReadOnly';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from 'react-query';
import {useStayDates} from '../../common/guestTable/guestTableCells';
import {serverTableControlIconSx, serverTableControlSx} from '../../common/serverTable/constants';
import {invitationGuestEmailLine, invitationGuestName} from './SurveyInvitationDetails';
import SurveyInviteHeader from './SurveyInviteHeader';
import {INVITE_EMAIL_PATTERN} from './surveyShare.constants';

/** Results shown at once; a search with more asks for a few more letters instead of paging. */
const SHOWN = 10;
export const SURVEY_INVITE_SEARCH_QUERY_KEY = 'survey-invite-search';
/** About five result rows: the area keeps this height while a search starts, so the dialog never collapses. */
const RESULTS_MIN_HEIGHT = 256;

const rowColumns = {display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px 132px', columnGap: 1.5, alignItems: 'center'};

interface Props {
    survey?: Survey
    /** False while another step shows; this step stays mounted so going back finds it as it was left. */
    active: boolean
    onPickGuest: (guest: ProfileListItem) => void
    onPickEmail: (email: string) => void
    onSendTest: () => void
    onClose: () => void
}

/**
 * The invite dialog's first step when the entry point knows nobody: one field for a name or an email. Picking a date
 * is the Daily List's job, so there is no list to browse; a full email nobody has can be invited as a new contact.
 */
export default function SurveyInviteSearch({survey, active, onPickGuest, onPickEmail, onSendTest, onClose}: Props) {
    const {t} = useTranslation();
    const readOnly = useReadOnly();
    const stayDates = useStayDates();
    const [input, setInput] = useState('');
    const [term, setTerm] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Back from another step: the cursor returns to the field, as on opening.
    useEffect(() => {
        if (active) inputRef.current?.focus();
    }, [active]);

    const debouncer = useDebouncer(setTerm, {wait: 400} as Parameters<typeof useDebouncer>[1]);
    const {cancel, maybeExecute} = debouncer;
    useEffect(() => () => cancel(), [cancel]);

    const results = useQuery(
        [SURVEY_INVITE_SEARCH_QUERY_KEY, term],
        () => ProfileAggregationService.searchGuests({
            query: term,
            limit: {offset: 0, limit: SHOWN, sort: [{field: 'name', direction: 'ASC'}]},
        }),
        // The last results stay while the next load, so the list never collapses between keystrokes.
        {enabled: !!term, keepPreviousData: true, refetchOnWindowFocus: false}
    );

    const clear = () => {
        cancel();
        setInput('');
        setTerm('');
    };

    const typed = input.trim();
    const guests = typed && term ? results.data?.content ?? [] : [];
    const more = (results.data?.totalElements ?? 0) > SHOWN;
    const email = INVITE_EMAIL_PATTERN.test(term) ? term : undefined;
    const emailIsAGuest = !!email && guests.some(guest => guest.primaryEmail?.toLowerCase() === email.toLowerCase());
    // The rows on screen answer the current term (not the last one kept while the next loads).
    const settled = !!term && results.isSuccess && !results.isPreviousData;
    const searching = (!!typed && typed !== term) || results.isFetching;

    return (
        <>
            <SurveyInviteHeader
                title={t('Create survey link')}
                subtitle={survey?.name}
                onClose={onClose}
                action={!readOnly && <Button onClick={onSendTest} sx={{flexShrink: 0}}>{t('Send test')}</Button>}
            >
                <TextField
                    autoFocus
                    inputRef={inputRef}
                    fullWidth
                    size={'small'}
                    value={input}
                    placeholder={t('Name or email')}
                    sx={{mt: 2}}
                    inputProps={{'aria-label': t('Search by name or email'), role: 'searchbox'}}
                    onChange={event => {
                        setInput(event.target.value);
                        maybeExecute(event.target.value.trim());
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment position={'start'}><SearchIcon fontSize={'small'}/></InputAdornment>,
                        endAdornment: input && (
                            <InputAdornment position={'end'}>
                                {searching && <CircularProgress size={16} sx={{mr: 1}}/>}
                                <IconButton size={'small'} aria-label={t('Clear search')} onClick={clear} sx={serverTableControlSx}>
                                    <CloseIcon sx={serverTableControlIconSx}/>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </SurveyInviteHeader>

            <DialogContent sx={{px: 2.5, pb: 2.5, '&&': {pt: 0}}}>
                {/* Never shorter than this, so starting a search can't collapse the dialog and pop it open again. */}
                <Stack sx={{minHeight: RESULTS_MIN_HEIGHT}}>
                    {!typed ? (
                        <Typography variant={'body1'} color={'text.secondary'} sx={{m: 'auto', textAlign: 'center'}}>
                            {t('Search a guest, or type any email address.')}
                        </Typography>
                    ) : results.isError ? (
                        <Alert severity={'error'} action={<Button onClick={() => void results.refetch()}>{t('Retry')}</Button>}>
                            {t('Guests could not be loaded.')}
                        </Alert>
                    ) : (
                        <>
                            <List disablePadding>
                                {guests.map(guest => {
                                    const stay = guest.selectedStay;
                                    return (
                                        <ListItemButton
                                            key={guest.id}
                                            divider
                                            onClick={() => onPickGuest(guest)}
                                            sx={{...rowColumns, px: 1, py: 1}}
                                        >
                                            <Box sx={{minWidth: 0}}>
                                                <Typography variant={'body1'} noWrap>{invitationGuestName(guest)}</Typography>
                                                {(guest.firstName || guest.lastName) && (
                                                    <Typography variant={'body2'} color={'text.secondary'} noWrap>
                                                        {invitationGuestEmailLine(guest) || t('No email')}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Typography variant={'body1'} noWrap>{stay?.roomId || '—'}</Typography>
                                            <Typography variant={'body1'} noWrap>
                                                {stayDates(stay?.startDay, stay?.endDay) || '—'}
                                            </Typography>
                                        </ListItemButton>
                                    );
                                })}
                                {email && !emailIsAGuest && settled && (
                                    <ListItemButton
                                        divider
                                        onClick={() => onPickEmail(email)}
                                        sx={{px: 1, py: 1.25, gap: 1, color: 'primary.main'}}
                                    >
                                        <MailOutlineIcon fontSize={'small'}/>
                                        <Typography variant={'body1'} noWrap>{t('Invite {{email}}', {email})}</Typography>
                                    </ListItemButton>
                                )}
                            </List>
                            {/* Belongs to the rows on screen, so it stays while the next letter loads. */}
                            {guests.length > 0 && more && (
                                <Typography variant={'body2'} color={'text.secondary'} sx={{pt: 1.5, px: 1}}>
                                    {t('Type more to narrow it down.')}
                                </Typography>
                            )}
                            {settled && !guests.length && !email && (
                                <Typography variant={'body1'} color={'text.secondary'} sx={{m: 'auto', textAlign: 'center'}}>
                                    {t('No guest found. Type a full email address to invite anyone.')}
                                </Typography>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
        </>
    );
}
